import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Slider,
  TextField,
  Typography,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';

interface ThresholdControlsProps {
  minProfitPercent: number;
  maxProfitPercent: number | null;
  minVolumeUSD: number;
  tradeAmountUSD: number;
  networkStatusFilter: {
    enabled: boolean;
    requireBothEnabled: boolean;
    hideUnknown: boolean;
    hideDisabled: boolean;
  };
  onProfitChange: (value: number) => void;
  onMaxProfitChange: (value: number | null) => void;
  onVolumeChange: (value: number) => void;
  onTradeAmountChange: (value: number) => void;
  onNetworkStatusFilterChange: (filter: {
    enabled: boolean;
    requireBothEnabled: boolean;
    hideUnknown: boolean;
    hideDisabled: boolean;
  }) => void;
}

const ThresholdControls: React.FC<ThresholdControlsProps> = ({
  minProfitPercent,
  maxProfitPercent,
  minVolumeUSD,
  tradeAmountUSD,
  networkStatusFilter,
  onProfitChange,
  onMaxProfitChange,
  onVolumeChange,
  onTradeAmountChange,
  onNetworkStatusFilterChange,
}) => {
  const { t } = useTranslation();
  const [volumeError, setVolumeError] = useState<string>('');
  const [volumeInput, setVolumeInput] = useState<string>(minVolumeUSD.toString());
  const [tradeAmountError, setTradeAmountError] = useState<string>('');
  const [tradeAmountInput, setTradeAmountInput] = useState<string>(tradeAmountUSD.toString());
  const [maxProfitEnabled, setMaxProfitEnabled] = useState<boolean>(maxProfitPercent !== null);
  const [maxProfitValue, setMaxProfitValue] = useState<number>(maxProfitPercent || 20);

  const handleProfitChange = (_: Event, newValue: number | number[]) => {
    onProfitChange(newValue as number);
  };

  const handleMaxProfitEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setMaxProfitEnabled(enabled);
    onMaxProfitChange(enabled ? maxProfitValue : null);
  };

  const handleMaxProfitChange = (_: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setMaxProfitValue(value);
    if (maxProfitEnabled) {
      onMaxProfitChange(value);
    }
  };

  const handleNetworkFilterEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNetworkStatusFilterChange({
      ...networkStatusFilter,
      enabled: event.target.checked,
    });
  };

  const handleRequireBothEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNetworkStatusFilterChange({
      ...networkStatusFilter,
      requireBothEnabled: event.target.checked,
    });
  };

  const handleHideUnknownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNetworkStatusFilterChange({
      ...networkStatusFilter,
      hideUnknown: event.target.checked,
    });
  };

  const handleHideDisabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNetworkStatusFilterChange({
      ...networkStatusFilter,
      hideDisabled: event.target.checked,
    });
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setVolumeInput(input);

    // Allow empty input for typing
    if (input === '') {
      setVolumeError('Volume is required');
      return;
    }

    const value = parseFloat(input);
    
    if (isNaN(value)) {
      setVolumeError('Please enter a valid number');
      return;
    }

    if (value < 100) {
      setVolumeError('Minimum volume must be at least $100');
      return;
    }

    if (value > 100000) {
      setVolumeError('Maximum volume is $100,000');
      return;
    }

    setVolumeError('');
    onVolumeChange(value);
  };

  const handleVolumeBlur = () => {
    // Reset to valid value on blur if invalid
    if (volumeError) {
      setVolumeInput(minVolumeUSD.toString());
      setVolumeError('');
    }
  };

  const handleTradeAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setTradeAmountInput(input);

    // Allow empty input - treat as 0 (unlimited)
    if (input === '') {
      setTradeAmountError('');
      onTradeAmountChange(0);
      return;
    }

    const value = parseFloat(input);
    
    if (isNaN(value)) {
      setTradeAmountError('Please enter a valid number');
      return;
    }

    if (value < 0) {
      setTradeAmountError('Trade amount cannot be negative');
      return;
    }

    if (value > 1000000) {
      setTradeAmountError('Maximum trade amount is $1,000,000');
      return;
    }

    setTradeAmountError('');
    onTradeAmountChange(value);
  };

  const handleTradeAmountBlur = () => {
    // Reset to valid value on blur if invalid
    if (tradeAmountError) {
      setTradeAmountInput(tradeAmountUSD.toString());
      setTradeAmountError('');
    }
    // If empty on blur, set to 0
    if (tradeAmountInput === '') {
      setTradeAmountInput('0');
      onTradeAmountChange(0);
    }
  };

  const profitMarks = [
    { value: 0.1, label: '0.1%' },
    { value: 1, label: '1%' },
    { value: 2.5, label: '2.5%' },
    { value: 5, label: '5%' },
  ];

  const maxProfitMarks = [
    { value: 10, label: '10%' },
    { value: 20, label: '20%' },
    { value: 30, label: '30%' },
    { value: 50, label: '50%' },
  ];

  return (
    <Box>
      {/* Profit Percentage Threshold */}
      <Box mb={3}>
        <Typography variant="body2" gutterBottom>
          {t('settings.filters.minProfitPercent')}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Slider
            value={minProfitPercent}
            onChange={handleProfitChange}
            min={0.1}
            max={5}
            step={0.1}
            marks={profitMarks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
            sx={{ flexGrow: 1 }}
          />
          <Typography variant="body2" sx={{ minWidth: 50 }}>
            {minProfitPercent.toFixed(1)}%
          </Typography>
        </Box>
      </Box>

      {/* Maximum Profit Percentage Filter */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={maxProfitEnabled}
                onChange={handleMaxProfitEnabledChange}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                {t('settings.filters.maxProfitPercent')}
              </Typography>
            }
          />
          <Tooltip 
            title="Filter out opportunities with unrealistic profit percentages. These are often due to different tokens, network incompatibilities, or disabled withdrawals/deposits."
            placement="top"
          >
            <Typography variant="caption" sx={{ cursor: 'help', color: 'text.secondary' }}>
              ℹ️
            </Typography>
          </Tooltip>
        </Box>
        {maxProfitEnabled && (
          <Box display="flex" alignItems="center" gap={2}>
            <Slider
              value={maxProfitValue}
              onChange={handleMaxProfitChange}
              min={5}
              max={50}
              step={1}
              marks={maxProfitMarks}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2" sx={{ minWidth: 50 }}>
              {maxProfitValue.toFixed(0)}%
            </Typography>
          </Box>
        )}
        {maxProfitEnabled && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Opportunities with profit &gt; {maxProfitValue}% will be filtered out
          </Typography>
        )}
      </Box>

      {/* Volume Threshold */}
      <Box>
        <Typography variant="body2" gutterBottom>
          {t('settings.filters.minVolumeUSD')}
        </Typography>
        <TextField
          value={volumeInput}
          onChange={handleVolumeChange}
          onBlur={handleVolumeBlur}
          type="number"
          fullWidth
          size="small"
          error={!!volumeError}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputProps: { min: 100, max: 100000, step: 100 }
          }}
          helperText={volumeError || 'Range: $100 - $100,000 (USDT equivalent for KRW/BTC pairs)'}
        />
      </Box>

      {/* Trade Amount - Hidden for now */}
      {/* <Box mt={3}>
        <Typography variant="body2" gutterBottom>
          Trade Amount (USD)
          <Tooltip title="Maximum amount to use per trade. Set to 0 for unlimited. This only affects profit calculations in the tooltip.">
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              (0 = unlimited)
            </Typography>
          </Tooltip>
        </Typography>
        <TextField
          value={tradeAmountInput}
          onChange={handleTradeAmountChange}
          onBlur={handleTradeAmountBlur}
          type="number"
          fullWidth
          size="small"
          error={!!tradeAmountError}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputProps: { min: 0, max: 1000000, step: 10 }
          }}
          helperText={tradeAmountError || 'Range: $0 (unlimited) - $1,000,000'}
        />
      </Box> */}

      {/* Network Status Filter */}
      <Box mt={3}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={networkStatusFilter.enabled}
                onChange={handleNetworkFilterEnabledChange}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                {t('settings.filters.networkFilters.enabled')}
              </Typography>
            }
          />
          <Tooltip 
            title="Only show opportunities where network transfers are possible based on deposit/withdrawal status"
            placement="top"
          >
            <Typography variant="caption" sx={{ cursor: 'help', color: 'text.secondary' }}>
              ℹ️
            </Typography>
          </Tooltip>
        </Box>
        {networkStatusFilter.enabled && (
          <Box sx={{ ml: 4 }}>
            <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
              {t('settings.filters.networkFilters.showWhere')}
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={networkStatusFilter.requireBothEnabled}
                  onChange={handleRequireBothEnabledChange}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="caption">
                    {t('settings.filters.networkFilters.bothEnabled')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('settings.filters.networkFilters.bothEnabledDesc')}
                  </Typography>
                </Box>
              }
            />
            
            <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mt: 2, mb: 1 }}>
              {t('settings.filters.networkFilters.hideWhere')}
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={networkStatusFilter.hideDisabled}
                  onChange={handleHideDisabledChange}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="caption">
                    {t('settings.filters.networkFilters.eitherDisabled')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('settings.filters.networkFilters.eitherDisabledDesc')}
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={networkStatusFilter.hideUnknown}
                  onChange={handleHideUnknownChange}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="caption">
                    {t('settings.filters.networkFilters.unknownStatus')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('settings.filters.networkFilters.unknownStatusDesc')}
                  </Typography>
                </Box>
              }
            />
            
            <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 2, p: 1, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}>
              {networkStatusFilter.requireBothEnabled && networkStatusFilter.hideDisabled && networkStatusFilter.hideUnknown
                ? t('settings.filters.networkFilters.maxFiltering')
                : networkStatusFilter.hideDisabled 
                  ? t('settings.filters.networkFilters.hidingNonTransferrable')
                  : t('settings.filters.networkFilters.showingAll')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ThresholdControls;